import { ReactNode } from 'react';
import { FieldValues, FormProvider, UseFormReturn } from 'react-hook-form';
import dynamic from 'next/dynamic';

import { TotalDepositProvider } from '../../contexts/TotalDepositContext';
import { StepLayout } from './StepLayout/StepLayout';
import { StepNavigation } from './StepNavigation/StepNavigation';
import { type StepConfig } from './types';
import type { UsePromptUserOnChangeBeforeLeavingModalParams } from '../../components/PromptUserOnChangeBeforeLeavingModal/PromptUserOnChangeBeforeLeavingModal.typed';

const PromptUserOnChangeBeforeLeavingModal =
  dynamic<UsePromptUserOnChangeBeforeLeavingModalParams>(
    import(
      '../../components/PromptUserOnChangeBeforeLeavingModal/PromptUserOnChangeBeforeLeavingModal'
    ).then((mod) => mod.PromptUserOnChangeBeforeLeavingModal),
  );

type ProviderFormShellProps<T extends FieldValues> = {
  methods: UseFormReturn<T>;
  minimumDepositValue: string;
  isDirty: boolean;
  currentStep: number;
  totalSteps: number;
  steps: StepConfig[];
  goToPrevStep: () => void;
  step1Content: ReactNode;
};

function ProviderFormShell<T extends FieldValues>({
  methods,
  minimumDepositValue,
  isDirty,
  currentStep,
  totalSteps,
  steps,
  goToPrevStep,
  step1Content,
}: ProviderFormShellProps<T>) {
  const activeContent = (() => {
    if (currentStep === 1) {
      return step1Content;
    }

    const { title, content, onNext, nextLabel, isSubmitting } =
      steps[currentStep - 2];

    return (
      <StepLayout
        title={title}
        currentStep={currentStep}
        totalSteps={totalSteps}
      >
        {content}
        <StepNavigation
          onBack={goToPrevStep}
          onNext={onNext}
          nextLabel={nextLabel}
          isSubmitting={isSubmitting}
        />
      </StepLayout>
    );
  })();

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={(e) => e.preventDefault()} noValidate>
          <TotalDepositProvider minimumDepositValue={minimumDepositValue}>
            {activeContent}
          </TotalDepositProvider>
        </form>
      </FormProvider>
      <PromptUserOnChangeBeforeLeavingModal promptUser={isDirty} />
    </>
  );
}

export { ProviderFormShell };
